import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Flame } from 'lucide-react';
import api from '../utils/axios';

const TABS = [
  { id: 'dia_hora', label: 'Día / Hora' },
  { id: 'tipo_severidad', label: 'Tipo / Severidad' },
  { id: 'estado_mes', label: 'Estado / Mes' },
];

const getColor = (val, max) => {
  if (max === 0 || val === 0) return 'bg-slate-50 text-slate-300';
  const ratio = val / max;
  if (ratio > 0.75) return 'bg-red-500 text-white font-bold';
  if (ratio > 0.5) return 'bg-orange-400 text-white';
  if (ratio > 0.25) return 'bg-amber-300 text-amber-900';
  if (ratio > 0.05) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-50 text-green-700';
};

const ActivityHeatmap = () => {
  const [tab, setTab] = useState('dia_hora');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/executive/activity-heatmap?tipo=${tab}`);
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    if (!data) return;
    let csv = ',' + data.columnas.join(',') + '\n';
    data.filas.forEach((fila, i) => {
      csv += fila + ',' + data.datos[i].join(',') + '\n';
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `heatmap_${tab}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const maxVal = data ? Math.max(...data.datos.flat(), 1) : 1;

  return (
    <Card className="p-0 overflow-hidden" data-testid="activity-heatmap">
      <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-orange-50 flex items-center justify-between">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
          <Flame size={18} className="text-orange-500" /> Heatmap de Actividad
        </h3>
        <Button size="sm" variant="outline" onClick={exportCSV} disabled={!data} className="text-xs">
          <Download size={14} className="mr-1" /> CSV
        </Button>
      </div>

      <div className="flex border-b border-slate-100">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 py-2 text-xs font-medium transition-colors ${
              tab === t.id
                ? 'bg-white text-slate-800 border-b-2 border-orange-500'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-3 overflow-x-auto" style={{ maxHeight: 400 }}>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">Cargando...</div>
        ) : data && data.filas.length > 0 ? (
          <table className="w-full text-[11px]">
            <thead>
              <tr>
                <th className="sticky left-0 bg-white z-10 text-left p-1 min-w-[100px] text-slate-600"></th>
                {data.columnas.map((col) => (
                  <th key={col} className="p-1 text-center text-slate-500 font-medium whitespace-nowrap">
                    {col.length > 7 ? col.slice(0, 7) : col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.filas.map((fila, i) => (
                <tr key={fila}>
                  <td className="sticky left-0 bg-white z-10 p-1 font-medium text-slate-700 whitespace-nowrap truncate max-w-[140px]" title={fila}>
                    {fila.length > 25 ? fila.slice(0, 25) + '...' : fila}
                  </td>
                  {data.datos[i].map((val, j) => (
                    <td key={j} className={`p-1 text-center rounded-sm ${getColor(val, maxVal)}`} title={`${fila} × ${data.columnas[j]}: ${val}`}>
                      {val > 0 ? val : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-slate-400 text-sm">Sin datos disponibles</div>
        )}
      </div>

      <div className="px-4 py-2 border-t border-slate-100 flex items-center gap-2 text-[10px] text-slate-400">
        <span>Escala:</span>
        <span className="px-2 py-0.5 bg-green-50 rounded">Baja</span>
        <span className="px-2 py-0.5 bg-yellow-100 rounded">Media</span>
        <span className="px-2 py-0.5 bg-amber-300 rounded">Alta</span>
        <span className="px-2 py-0.5 bg-orange-400 text-white rounded">Muy Alta</span>
        <span className="px-2 py-0.5 bg-red-500 text-white rounded">Critica</span>
      </div>
    </Card>
  );
};

export default ActivityHeatmap;
